import axios from 'axios';
import { useEffect, useState } from 'react';
import { FaBell, FaCheckCircle, FaExternalLinkAlt, FaIdCard, FaStore } from 'react-icons/fa';
import authHeader from '../services/auth-header';
import './adminEvents.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

type AdminEvent = {
  id: number; type: 'SOLICITUD_PROVEEDOR'; title: string; message: string;
  read: boolean; providerApplicationId: number; createdAt: string;
};

type ProviderApplication = {
  id: number; accountType: 'PERSON' | 'BUSINESS'; plan: 'BASIC' | 'PREMIUM'; status: string;
  fullName?: string; businessName?: string; representativeName?: string; email: string; phone: string;
  description: string; storePickupEnabled: boolean; iban: string; termsAccepted: boolean;
  termsAcceptedAt: string; identityFrontUrl: string; identityBackUrl: string; identitySelfieUrl: string; createdAt: string;
};

const dateTime = (value: string) => new Intl.DateTimeFormat('es-CR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));

const AdminEvents = () => {
  const [events, setEvents] = useState<AdminEvent[]>([]);
  const [selected, setSelected] = useState<ProviderApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/admin/events`, { headers: authHeader() })
      .then(response => setEvents(response.data))
      .catch(requestError => setError(requestError.response?.data?.message || 'No fue posible cargar los eventos.'))
      .finally(() => setLoading(false));
  }, []);

  const openApplication = async (event: AdminEvent) => {
    setDetailLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/admin/provider-applications/${event.providerApplicationId}`, { headers: authHeader() });
      setSelected(response.data);
      setEvents(current => current.map(item => item.id === event.id ? { ...item, read: true } : item));
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No fue posible abrir la solicitud.');
    } finally {
      setDetailLoading(false);
    }
  };

  return <div className="admin-events-page">
    <div className="admin-events-heading">
      <div><span>Administración</span><h1>Eventos</h1><p>Solicitudes y actividades que requieren revisión.</p></div>
      <div className="admin-events-count"><FaBell /><strong>{events.filter(event => !event.read).length}</strong><span>pendientes</span></div>
    </div>
    {error && <div className="alert alert-danger">{error}</div>}
    <div className="admin-events-layout">
      <section className="admin-events-list">
        {loading && <p className="admin-events-empty">Cargando eventos…</p>}
        {!loading && events.length === 0 && <div className="admin-events-empty"><FaCheckCircle /><h2>Todo está al día</h2><p>No hay eventos para revisar.</p></div>}
        {events.map(event => <button key={event.id} className={`admin-event-row ${!event.read ? 'is-unread' : ''} ${selected?.id === event.providerApplicationId ? 'is-selected' : ''}`} onClick={() => openApplication(event)}>
          <div className="admin-event-icon"><FaStore /></div>
          <div><div className="admin-event-meta"><span>{event.type.replaceAll('_', ' ')}</span>{!event.read && <i>NUEVO</i>}</div><h2>{event.title}</h2><p>{event.message}</p><small>{dateTime(event.createdAt)}</small></div>
        </button>)}
      </section>
      <section className="admin-event-detail">
        {detailLoading && <p className="admin-events-empty">Cargando expediente…</p>}
        {!detailLoading && !selected && <div className="admin-events-empty"><FaIdCard /><h2>Selecciona un evento</h2><p>Aquí podrás revisar todos los datos y documentos del solicitante.</p></div>}
        {!detailLoading && selected && <>
          <div className="admin-detail-header"><div><span>Solicitud #{selected.id}</span><h2>{selected.accountType === 'BUSINESS' ? selected.businessName : selected.fullName}</h2></div><strong>{selected.status === 'SUBMITTED' ? 'Recibida' : selected.status}</strong></div>
          <div className="admin-detail-grid">
            <div><span>Tipo</span><strong>{selected.accountType === 'PERSON' ? 'Persona física' : 'Tienda / negocio'}</strong></div>
            <div><span>Plan</span><strong>{selected.plan === 'BASIC' ? 'Básico' : 'Premium'}</strong></div>
            {selected.representativeName && <div><span>Representante</span><strong>{selected.representativeName}</strong></div>}
            <div><span>Correo</span><strong>{selected.email}</strong></div>
            <div><span>Teléfono</span><strong>{selected.phone}</strong></div>
            <div><span>Retiro en tienda</span><strong>{selected.storePickupEnabled ? 'Sí' : 'No'}</strong></div>
            <div className="admin-detail-wide"><span>Descripción</span><p>{selected.description}</p></div>
            <div className="admin-detail-wide"><span>Cuenta IBAN</span><strong>{selected.iban}</strong></div>
            <div><span>Términos aceptados</span><strong>{selected.termsAccepted ? 'Sí' : 'No'}</strong></div>
            <div><span>Fecha de envío</span><strong>{dateTime(selected.createdAt)}</strong></div>
          </div>
          <div className="admin-documents"><h3>Verificación de identidad</h3><div>
            <a href={selected.identityFrontUrl} target="_blank" rel="noreferrer"><FaIdCard /> Cédula · frente <FaExternalLinkAlt /></a>
            <a href={selected.identityBackUrl} target="_blank" rel="noreferrer"><FaIdCard /> Cédula · reverso <FaExternalLinkAlt /></a>
            <a href={selected.identitySelfieUrl} target="_blank" rel="noreferrer"><FaIdCard /> Selfie con documento <FaExternalLinkAlt /></a>
          </div></div>
          <div className="admin-approval-next"><strong>Próxima fase</strong><p>La aprobación y creación automática del usuario con <code>ROLE_PROVIDER</code> se habilitarán en la siguiente etapa.</p></div>
        </>}
      </section>
    </div>
  </div>;
};

export default AdminEvents;
